import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { Spinner, SectionLabel } from "../components/Primitives";
import FileCard from "../components/FileCard";
import FileModal from "../components/FileModal";
import { cn } from "../lib/utils";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "image", label: "Images" },
  { key: "pdf", label: "Documents" },
  { key: "video", label: "Video" },
  { key: "audio", label: "Audio" },
];

export default function Resources() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/files").then((r) => setFiles(r.data)).finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => {
    if (filter === "all") return files;
    if (filter === "pdf") return files.filter((f) => f.kind === "pdf" || f.kind === "text" || f.kind === "document");
    return files.filter((f) => f.kind === filter);
  }, [files, filter]);

  return (
    <div data-testid="resources-page">
      <div className="border-b border-edge bg-ash">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <SectionLabel>Library</SectionLabel>
          <h1 className="font-serif text-5xl font-semibold text-bone lg:text-6xl">Curriculum Resources</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted">
            Primary sources, readings, images, and media — curated to view directly in your browser.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={cn(
                  "border px-4 py-2 text-sm transition-colors",
                  filter === f.key ? "border-gold bg-gold text-ink" : "border-edge text-muted hover:border-gold/50"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-12">
          {shown.length === 0 ? (
            <p className="border border-dashed border-edge p-16 text-center text-muted" data-testid="empty-resources">
              No resources here yet. An administrator can add materials from the Admin panel.
            </p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((f, i) => (
                <motion.div key={f.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 6) * 0.05 }}>
                  <FileCard file={f} onOpen={setActive} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {active && <FileModal file={active} onClose={() => setActive(null)} />}
    </div>
  );
}
